import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from 'src/config-global';

import type { ParametersQueryUser } from './types';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.site.serverUrl });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong!')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------
type QueryScalar = string | number | boolean;
type QueryValue = QueryScalar | QueryScalar[] | null | undefined;

export function buildQuery<T extends Record<string, QueryValue>>(params?: T): string {
  if (!params) return '';
  const q = new URLSearchParams();

  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;

    if (Array.isArray(v)) {
      q.set(k, v.map((item) => String(item)).join(','));
    } else {
      q.set(k, String(v));
    }
  });

  const s = q.toString();
  return s ? `?${s}` : '';
}
// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  upload: '/upload',
  uploads: '/upload/multiple',
  lecture: {
    assignmentList: '/lecture/assignment-list',
  },
  message: {
    postConversation: `/message/conversations/messages`,
  },
  audit: {
    file: (date: string, download: '0' | '1' = '0') => `/audit/files/${date}?download=${download}`,
  },
  notification: {
    NOTIFICATIONS_MINE: '/notifications/mine',
    NOTIFICATIONS: '/notifications',
    NOTIFICATION_BY_ID: (id: string) => `/notifications/${id}`,
    MARK: (id: string) => `/notifications/${id}/read`,
    MARK_READ_BULK: '/notifications/read/bulk',
    ADMIN: (querySearch: string) => `admin/notifications${querySearch}`,
    ADMIN_DETAIL_ID: (id: string) => `admin/notifications/${id}?withRecipients=true`,
  },
  zoom: {
    getParticipants: (meetingId: string) => `/zoom/get-participants/${meetingId}`,
    getMettings: '/zoom/meetings',
    getMettingById: (meetingId: string) => `/zoom/get-meeting/${meetingId}`,
    getMeetingTemplates: '/zoom/meeting-templates',
    createMeeting: '/zoom/create-meeting',
    generateSignature: '/zoom/generate-signature',
    sendZoomParticipant: '/zoom/participant',
    leaveParticipant: '/zoom/participant/leave',
    activityLog: '/zoom/activity-log',
    delete: (meetingId: string) => '',
    end: (meetingId: string) => '',
  },
  question: {
    createByExcel: '/questions/add-by-excel',
  },
  log: {
    accessLog: '/access-logs',
  },
  exam: {
    create: '/exam/create',
    get: '/exam',
    update: (examId: string) => `/exam/${examId}`,
    delete: (examId: string) => `/exam/${examId}`,
    getBySlug: (slug: string) => `/exam/${slug}/student`,
    submitExam: '/exam/submit',
    report: (examId: string) => `/exam/${examId}/users/report`,
    updateScore: (userExamId: string) => `/exam/${userExamId}/update-score`,
    listByUser: `/exam/student`,
  },
  orders: {
    getAll: 'order',
  },
  groups: {
    userInGouprs: 'users/groups',
  },
  users: {
    customizeValue: '/users/value-user',
  },
  courses: {
    learningProgress: '/courses/user/learning-progress',
    limitStudyTime: (courseId: string) => `/courses/${courseId}/study-time/limit`,
    customizeValue: '/courses/value-courses',
    addStudentToCourse: (courseId: string) => `/courses/${courseId}/add-student-to-course`,
    removeStudentToCourse: (courseId: string) => `/courses/${courseId}/remove-student-from-course`,
    askCourse: (courseId: string, query: string) => `/courses/${courseId}/asks${query}`,
    answerCourseAsk: (courseId: string, askId: string) =>
      `/courses/${courseId}/asks/${askId}/answer`,
    createCourseAsk: (courseId: string) => `/courses/${courseId}/asks`,
  },
  schedule: {
    create: '/schedule',
    get: '/schedule',
  },
  attendance: {
    root: '/attendance',
    root_2: (scheduleId: string) => `/attendance/${scheduleId}`,
    getByCourseId: (courseId: string) => `/attendance/course/${courseId}`,
    getByScheduleId: (scheduleId: string) => `/schedule/attendance-records/${scheduleId}`,
    sentClassInviteEmail: (courseId: string, scheuleId: string) =>
      `/courses/${courseId}/sent-class-invite-email/${scheuleId}`,
  },
  admin: {
    learningProgressForUser: (userId: string) => `/users/admin-learning-progress/${userId}`,
    selectNotification: 'notifications',
  },
  auth: {
    me: '/auth/me',
    signIn: '/auth/sign-in',
    logOut: '/auth/logout',
    forgotPassword: '/auth/forgot-password',
    validCode: '/auth/forgot-password/valid-code',
    changePassword: '/auth/forgot-password/change-password',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },

  // corse

  dashboard: {
    root: '/api/courses/get-list-in-dashboard',
    manage: {
      user: {
        get: ({
          pageSize = 8,
          currentPage,
          orderBy = 'id',
          search = '',
          type = 'list',
        }: ParametersQueryUser) =>
          `/api/admin/user/paging?type=${type}&pageSize=${pageSize}&currentPage=${currentPage}&orderBy=${orderBy}&search=${search}`,
        getNameIdByRole: '/users/get-name-id-by-role',
        insertUser: '/users/create-user-information',
        saveByExcel: '/users/create-user-information-excel',
        getAllUsers: '/users/get-all-user',
        updateUser: (userId: string) => `/users/update-user-by-id/${userId}`,
        addWorkExps: (userId: string) => `/users/add-work-exps/${userId}`,
        addTeachingExps: (userId: string) => `/users/add-teaching-exps/${userId}`,
        addFieldConsulting: (userId: string) => `/users/add-field-consulting/${userId}`,
        addQualifications: (userId: string) => `/users/add-qualification/${userId}`,
        getUserValue: '/users/value-user',
        course: {
          learningProgress: (userId: string) => `/courses/get-course-user-id/${userId}`,
        },
        groups: {
          new: '/groups/add',
          update: (groupId: string) => `/groups/update/${groupId}`,
        },
        learningProgress: '/users/v2-beta/learning-progress',
        checkPreLearningMessage: (courseId: string) => `/courses/${courseId}/check-message`,
      },
      news: {
        insert: '/news',
        get: '/news/get',
      },
      report: {
        course: {
          detail: '/export-report/k04/details',
          weekly: '/export-report/weekly',
        },
      },
      course: {
        getUserInCourse: (courseId: string) => `/courses/${courseId}/users`,
        v2: {
          insert: '/courses/v2/create',
          get: '/courses/v2/get-all',
          getCourseById: (courseId: string) => `courses/v2/${courseId}`,
          delete: (courseId: string) => `/courses/v2/delete/${courseId}`,
          update: (courseId: string) => `/courses/${courseId}/update`,
          addChild: (courseId: string) => `/courses/v2/${courseId}/add-child`,
          customFields: '/courses/v2/custom-fields',
          inquiries: (courseId: string) => `/courses/${courseId}/inquiries`,
          documents: (courseId: string) => `/courses/${courseId}/documents`,
          addUserInCourse: (courseId: string) => `/courses/v2/${courseId}/add-user`,
          essay: {
            get: (courseId: string) => `/courses/v2/${courseId}/essay-submissions`,
            grade: (submissionId: string) => `/courses/v2/essays/${submissionId}/grade`,
          },
        },

        getCourseByUser: '/courses/get-course-by-user',
        getAllCourse: '/courses/get-all-course',
        create: '/courses/add-courses',
        getNameAndId: 'courses/get-name-id-course',
        addLecture: '/courses/add-lecture',
        getCourseContent: (courseId: string) => `/courses/${courseId}/content`,
        updateCourseContent: 'courses/update-lecture',
        getCourseValue: '/courses/value-courses',
        updateInformationCouse: (courseId: string) => `/courses/update-course-info/${courseId}`,
        getCourseDetailById: (courseId: string) => `/courses/get-course-detail-by-id/${courseId}`,
        getContribution: '/users/v2/contributed-courses',
        deleteChapter: (chapterId: string) => `/courses/delete-chapter/${chapterId}`,
        deleteLecture: (lectureId: string) => `/lecture/v2/${lectureId}/delete`,
        addUserInCourse: (courseId: string) => `/courses/v2/${courseId}/add-user`,
        ask: {
          getAllAskInCourse: (courseId: string) => `/ask/get-all-ask-in-course/${courseId}`,
          addAnswerAskCourse: (askId: string) => `/ask/answer-ask-course/${askId}`,
        },
        document: {
          new: '/course-documents/create',
        },
        getStudentsInCourse: (courseId: string) => `/courses/${courseId}/students`,
        getStudentStatisticsCourse: (courseId: string) =>
          `/courses/get-all-users-in-course/${courseId}`,
      },
      folder: {
        get: '/files/get-all',
      },
      survey: {
        get: '/questions/get-all-survey',
      },
      categories: {
        get: '/courses/select-categories',
        new: '/courses/add-category',
        getAll: '/category/get-all',
        orderUpdate: '/category/order-update',
      },
      chapter: {
        delete: (chapterId: string) => `/courses/delete-chapter/${chapterId}`,
      },
      question: {
        get: '/questions/get-all-question',
        new: '/questions/add-question',
        update: (questionId: string) => `/questions/update-question/${questionId}`,
        delete: '/questions/delete-question',
        group: {
          get: '/questions/get-all-question-groups',
          new: '/questions/add-question-group',
        },
        survey: {
          get: '/questions/get-all-survey',
          new: '/questions/add-survey',
          report: (surveyId: string) => `/survey/${surveyId}/report`,
        },
      },
      exams: {
        new: '/exams/create',
        update: '/exams/update',
      },
      partners: {
        get: '/partner/get',
      },
      groups: {
        get: '/groups/get',
      },
      departments: {
        get: '/departments/all',
        new: 'departments/add-departments',
      },
      positions: {
        get: '/positions/all',
        new: '/positions/add-positions',
      },
      areas: {
        get: '/areas/all',
        new: '/areas/add-areas',
      },
      notify: {
        new: '/notifications/create-sent',
      },
      cerfiticate: {
        insert: '/certificate/add',
        get: '/certificate/get',
      },
    },
    administrator: {
      user: {
        get: '/dashboard/administrator/user',
      },
      bunny: {
        get: '/dashboard/administrator/bunny',
      },
      course: {
        get: '/dashboard/administrator/course',
      },
    },
  },
  mentor: {
    root: () => `users/get-name-id-by-role`,
    mentorInfo: (mentorId: string) => `/users/get-mentor-info/${mentorId}`,
    createMentoting: '/mentoring/create',
    getMentoring: '/mentoring/get-by-user',
  },
  client: {
    dashboard: '/dashboard/get',
    getCourseClient: '/courses/get-course-by-user',
    getCourseSlug: (slug: string) => `/courses/v3/content/${slug}`,
    submitLecture: (lectureId: string) => `/lecture/v2/${lectureId}/submit-assignment`,
    submitAssignment: `/lecture/submit-assignment`,
    getSurveyBySlug: (slug: string) => `/questions/get-survey-by-slug/${slug}`,
    getSurveyResultBySlug: (slug: string) => `/survey/get-result-by-slug/${slug}`,
    submitSurvey: '/survey/submit',
    submitUserOrder: 'courses/update-course-order',
    user: {
      verifyResetPasswordToken: '/auth/reset-password',
      sendEmailResetPassword: '/auth/send-reset-password',
      update: '/users/update-information',
      loginHistory: '/users/login-history',
    },
    notification: {
      enable: '/users/push/enable',
    },
    // Chat
    course: {
      askCreate: '/ask/create',
      getAskUserAndCourse: (courseId: string) => `/ask/get-ask-course-user-course/${courseId}`,
      getDocument: (courseId: string) => `/course-documents/get-document-by-course/${courseId}`,
      addContribution: '/courses/add-contribution',
      markMessageAsReadBeforeCourse: (messageId: string, courseId: string) =>
        `/courses/${courseId}/messages/${messageId}/read`,
    },
    chat: {
      getRoomChat: (roomCode: string) => `/chat-room/get-room-chat/${roomCode}`,
    },
    ranking: {
      getRanking: '/ranking',
    },
  },
  course: {
    myCourse: (paging: number, idUser: number) =>
      `api/courses/my-courses/paging/${paging}/${idUser}`,
  },
};
